// core/services/badge/badge-kv.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConfig } from '../config.service';
import { SessionQuery } from '../../state-mangement/session/session.query';
import { BadgeQuery } from '../../state-mangement/badge/badge.query';
import { BadgeService } from '../../state-mangement/badge/badge.service';
import { TableColumn } from '../../models/table-column';

type KvBadge = {
  name: string;
  selected: boolean;
  position: number;
  disabled: boolean;
};

@Injectable({ providedIn: 'root' })
export class BadgeKvService {
  private readonly groupKey = 'Badge';
  private readonly names = [
    'WorkInfo',
    'Saldo',
    'Note',
    'Material',
    'Quantity',
    'Expense',
    'Terminate',
  ] as const;
  private readonly url =
    (AppConfig?.settings?.backend?.url || '/api/') + 'Settings/getSetting';

  constructor(
    private http: HttpClient,
    private sessionQuery: SessionQuery,
    private badgeQuery: BadgeQuery,
    private badgeService: BadgeService
  ) {}

  loadAndApply() {
    const calls = this.names.map((n) =>
      this.get$(n).pipe(
        map((r) => this.parse(n, r)),
        catchError(() => of<KvBadge | null>(null))
      )
    );

    forkJoin(calls).subscribe((list) => {
      const kv = list.filter(Boolean) as KvBadge[];
      if (!kv.length) return;

      const current = this.badgeQuery.getSettings(); // Defaults im Store
      const merged: TableColumn[] = current
        .map((c) => {
          const hit = kv.find((k) => k.name === c.name);
          return hit
            ? {
                ...c,
                selected: hit.selected,
                position: hit.position,
                disabled: hit.disabled,
              }
            : c;
        })
        .sort((a, b) => a.position - b.position);

      this.badgeService.saveSettings(merged);
    });
  }

  private get$(nameKey: string) {
    let p = new HttpParams()
      .set('GroupKey', this.groupKey)
      .set('NameKey', nameKey);
    const pid = this.sessionQuery.getPersonID();
    const uid = this.sessionQuery.getAdmiaUserID();
    if (pid) p = p.set('IDPerson', String(pid));
    if (uid) p = p.set('IDAdmiaUser', String(uid));
    return this.http.get<any>(this.url, { params: p });
  }

  private parse(name: string, res: any): KvBadge | null {
    if (!res) return null;
    // bevorzugt ValueString {"selected":..., "position":..., "disabled":...}
    if (res.valueString) {
      try {
        const j = JSON.parse(res.valueString);
        return {
          name,
          selected: !!j.selected,
          position: Number.isFinite(+j.position) ? +j.position : 0,
          disabled: !!j.disabled,
        };
      } catch {
        /* fallthrough */
      }
    }
    // oder einzelne Spalten nutzen (falls du mal dahin migrierst)
    return {
      name,
      selected: typeof res.valueBool === 'boolean' ? res.valueBool : false,
      position: typeof res.valueInt === 'number' ? res.valueInt : 0,
      disabled:
        typeof res.valueDecimal === 'number' ? !!res.valueDecimal : false,
    };
  }
}
